import { useMemo } from 'react';
import {
  Card,
  Col,
  Row,
  Statistic,
  Table,
  Typography,
  Spin,
  Alert,
} from 'antd';
import {
  DollarOutlined,
  RiseOutlined,
  ShoppingCartOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useSalesDashboard } from '@hooks/useAnalytics';
import type { TopCustomer, TopProduct, MonthlySales } from '@/types/analytics';

export default function SalesDashboardPage() {
  const { data: mdData, isLoading: mdIsLoading } = useSalesDashboard();

  const mdMaxRevenue = useMemo(() => {
    if (!mdData?.salesTrend || mdData.salesTrend.length === 0) return 1;
    return Math.max(...mdData.salesTrend.map((mdItem) => mdItem.revenue), 1);
  }, [mdData]);

  const mdMaxOrders = useMemo(() => {
    if (!mdData?.salesTrend || mdData.salesTrend.length === 0) return 1;
    return Math.max(...mdData.salesTrend.map((mdItem) => mdItem.orderCount), 1);
  }, [mdData]);

  const mdMonthNames = [
    'Січ', 'Лют', 'Бер', 'Кві', 'Тра', 'Чер',
    'Лип', 'Сер', 'Вер', 'Жов', 'Лис', 'Гру',
  ];

  const mdCustomerColumns: ColumnsType<TopCustomer> = [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_mdValue, _mdRecord, mdIndex) => mdIndex + 1,
    },
    {
      title: 'Клієнт',
      dataIndex: 'customerName',
      key: 'customerName',
      ellipsis: true,
    },
    {
      title: 'Виручка, UAH',
      dataIndex: 'revenue',
      key: 'revenue',
      width: 160,
      align: 'right',
      render: (mdValue: number) =>
        mdValue.toLocaleString('uk-UA', { minimumFractionDigits: 0 }),
    },
    {
      title: 'К-ть замовлень',
      dataIndex: 'orderCount',
      key: 'orderCount',
      width: 140,
      align: 'right',
    },
  ];

  const mdProductColumns: ColumnsType<TopProduct> = [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_mdValue, _mdRecord, mdIndex) => mdIndex + 1,
    },
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      width: 120,
    },
    {
      title: 'Назва',
      dataIndex: 'productName',
      key: 'productName',
      ellipsis: true,
    },
    {
      title: 'Продано (шт)',
      dataIndex: 'quantitySold',
      key: 'quantitySold',
      width: 120,
      align: 'right',
    },
    {
      title: 'Виручка, UAH',
      dataIndex: 'revenue',
      key: 'revenue',
      width: 160,
      align: 'right',
      render: (mdValue: number) =>
        mdValue.toLocaleString('uk-UA', { minimumFractionDigits: 0 }),
    },
  ];

  if (mdIsLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!mdData) {
    return <Alert type="warning" message="Дані продажів недоступні" showIcon />;
  }

  return (
    <>
      <Typography.Title level={4} style={{ marginBottom: 16 }}>
        Дашборд продажів
      </Typography.Title>

      {/* Summary row */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Виручка за місяць"
              value={mdData.monthlyRevenue}
              precision={0}
              prefix={<DollarOutlined />}
              suffix="UAH"
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Маржа"
              value={mdData.monthlyMargin}
              precision={1}
              prefix={<RiseOutlined />}
              suffix="%"
              valueStyle={{ color: mdData.monthlyMargin >= 20 ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Нових замовлень"
              value={mdData.newOrdersThisMonth}
              prefix={<ShoppingCartOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Доставлених"
              value={mdData.deliveredOrdersThisMonth}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Sales trend chart - simple bar chart */}
      <Card title="Тренд продажів (12 місяців)" size="small" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', height: 200, padding: '0 8px' }}>
          {mdData.salesTrend.map((mdItem: MonthlySales) => {
            const mdRevenueHeight = (mdItem.revenue / mdMaxRevenue) * 160;
            const mdOrderHeight = (mdItem.orderCount / mdMaxOrders) * 160;
            const mdMonthLabel = mdMonthNames[mdItem.month - 1] ?? String(mdItem.month);
            return (
              <div
                key={`${mdItem.year}-${mdItem.month}`}
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 170 }}>
                  <div
                    title={`Виручка: ${mdItem.revenue.toLocaleString('uk-UA')} UAH`}
                    style={{
                      width: 16,
                      height: Math.max(mdRevenueHeight, 2),
                      backgroundColor: '#1677ff',
                      borderRadius: '2px 2px 0 0',
                    }}
                  />
                  <div
                    title={`Замовлень: ${mdItem.orderCount}`}
                    style={{
                      width: 16,
                      height: Math.max(mdOrderHeight, 2),
                      backgroundColor: '#52c41a',
                      borderRadius: '2px 2px 0 0',
                    }}
                  />
                </div>
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: 11, lineHeight: '16px' }}
                >
                  {mdMonthLabel}
                </Typography.Text>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 24, justifyContent: 'center', marginTop: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: '#1677ff' }} />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>Виручка</Typography.Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: '#52c41a' }} />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>Замовлення</Typography.Text>
          </div>
        </div>
      </Card>

      {/* Two tables side by side */}
      <Row gutter={16}>
        <Col span={12}>
          <Card title="Топ-10 клієнтів" size="small">
            <Table<TopCustomer>
              columns={mdCustomerColumns}
              dataSource={mdData.topCustomers}
              rowKey="customerId"
              pagination={false}
              size="small"
              locale={{ emptyText: 'Немає даних' }}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Топ-10 товарів" size="small">
            <Table<TopProduct>
              columns={mdProductColumns}
              dataSource={mdData.topProducts}
              rowKey="productId"
              pagination={false}
              size="small"
              locale={{ emptyText: 'Немає даних' }}
            />
          </Card>
        </Col>
      </Row>
    </>
  );
}
